/**
 * The smallest useful half of the XLSX format: read the cells out of a
 * workbook, and write one that Excel, Numbers and Sheets all open.
 *
 * An `.xlsx` is a ZIP of XML parts, both of which the platform already knows
 * how to handle — `DecompressionStream`/`CompressionStream` do the deflate, and
 * the parts this reads are a constrained enough subset of SpreadsheetML to walk
 * directly. That is why there is no spreadsheet dependency here: the ones on
 * offer are megabytes of format coverage (charts, pivot tables, formulas,
 * styles) to do a job that is, for a customer list, "give me the strings in the
 * grid" and "put these strings in a grid".
 *
 * What it deliberately does not do: formulas are read as their last cached
 * result, and a date cell arrives as the serial number Excel stores rather than
 * a date, because nothing imported here is a date. Both are visible in the
 * import preview before anything is saved. Number formats are read only far
 * enough to put back the leading zeros of a code like 01234, which the file
 * stores as the number 1234.
 */

import type { TableRow } from './spreadsheet.ts';

/**
 * Why a workbook could not be read.
 *
 * A code rather than a sentence: the only place these are shown is the import
 * screen, which renders them in the reader's own language, and a message
 * assembled down here could only ever be in one.
 */
export type XlsxErrorCode =
  'notArchive' | 'noWorkbook' | 'noSheets' | 'damaged' | 'unsupportedCompression' | 'tooLarge';

export class XlsxError extends Error {
  constructor(
    readonly code: XlsxErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'XlsxError';
  }
}

/** A value a cell can carry on the way out. Null writes an empty cell. */
export type XlsxCell = string | number | boolean | null;

export interface XlsxSheet {
  name: string;
  /**
   * Ragged: exactly as wide as each row was, trailing empty cells trimmed, and
   * each carrying the worksheet row it came from.
   */
  rows: TableRow[];
}

/** Guard against a crafted or corrupt archive claiming an absurd part size. */
const MAX_PART_BYTES = 64 * 1024 * 1024;

/**
 * How far right a cell can sit before it is taken for debris.
 *
 * A worksheet addresses its cells rather than listing them, so one stray value
 * in Excel's last column would otherwise expand every row it appears in to
 * 16,384 cells — and the importer draws one mapping control per column, which
 * is a frozen tab over a cell nobody meant to type. No customer list is this
 * wide; anything beyond it is a leftover, and leftovers are dropped.
 */
const MAX_COLUMNS = 512;

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

/**
 * Every worksheet in a workbook, in the order the workbook lists them.
 *
 * All of them rather than just the first: an export from another system often
 * arrives with its data on the second tab behind a cover sheet, and silently
 * importing whichever one happened to be first is how somebody ends up with a
 * customer named "Generated by".
 */
export async function readXlsx(
  data: ArrayBuffer | Uint8Array,
  options: { maxRows?: number } = {},
): Promise<XlsxSheet[]> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const entries = readCentralDirectory(bytes);

  const readText = async (name: string): Promise<string | null> => {
    const entry = entries.get(name);
    if (!entry) return null;
    return new TextDecoder().decode(await readEntry(bytes, entry));
  };

  const workbookXml = await readText('xl/workbook.xml');
  if (workbookXml === null) {
    throw new XlsxError('noWorkbook', 'That file is not a spreadsheet — it has no workbook inside.');
  }

  const relationships = parseRelationships(await readText('xl/_rels/workbook.xml.rels'));
  const sharedStrings = parseSharedStrings(await readText('xl/sharedStrings.xml'));
  const numberFormats = parseNumberFormats(await readText('xl/styles.xml'));

  const sheets: XlsxSheet[] = [];
  for (const { name, relationshipId } of parseSheetIndex(workbookXml)) {
    const target = relationshipId ? relationships.get(relationshipId) : undefined;
    const path = target ? resolveSheetPath(target) : null;
    const sheetXml = path ? await readText(path) : null;
    if (sheetXml === null) continue;
    sheets.push({
      name,
      rows: parseSheetRows(sheetXml, sharedStrings, numberFormats, options.maxRows ?? Number.POSITIVE_INFINITY),
    });
  }

  if (sheets.length === 0) throw new XlsxError('noSheets', 'That workbook has no readable sheets.');
  return sheets;
}

/** `/xl/worksheets/sheet1.xml` and `worksheets/sheet1.xml` name the same part. */
function resolveSheetPath(target: string): string {
  const trimmed = target.startsWith('/') ? target.slice(1) : target;
  return trimmed.startsWith('xl/') ? trimmed : `xl/${trimmed}`;
}

function parseSheetIndex(workbookXml: string): Array<{ name: string; relationshipId: string | null }> {
  const sheets: Array<{ name: string; relationshipId: string | null }> = [];
  for (const match of workbookXml.matchAll(/<sheet\s[^>]*\/?>/g)) {
    const tag = match[0];
    sheets.push({
      name: decodeXml(attribute(tag, 'name') ?? `Sheet ${sheets.length + 1}`),
      // The namespace prefix is `r` by convention but is not guaranteed to be,
      // so the local name is what is matched.
      relationshipId: attribute(tag, 'r:id') ?? attribute(tag, 'id'),
    });
  }
  return sheets;
}

function parseRelationships(xml: string | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!xml) return map;
  for (const match of xml.matchAll(/<Relationship\s[^>]*\/?>/g)) {
    const id = attribute(match[0], 'Id');
    const target = attribute(match[0], 'Target');
    if (id && target) map.set(id, decodeXml(target));
  }
  return map;
}

/**
 * The workbook's string pool.
 *
 * Cells of type `s` hold an index into this rather than their own text, which
 * is how a spreadsheet with the same city in ten thousand rows stays small.
 */
function parseSharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const strings: string[] = [];
  for (const match of xml.matchAll(/<si(?:\s[^>]*)?>([\s\S]*?)<\/si>|<si(?:\s[^>]*)?\/>/g)) {
    strings.push(collectText(match[1] ?? ''));
  }
  return strings;
}

/**
 * The text of a string item, with its runs joined.
 *
 * A cell whose text is partly bold is stored as several `<r>` runs, each with
 * its own `<t>`; the value is all of them in order. Phonetic guides (`<rPh>`,
 * the furigana a Japanese edition writes above a name) carry `<t>` of their own
 * and are dropped — they are an annotation on the value, not part of it.
 */
function collectText(fragment: string): string {
  const withoutPhonetics = fragment.replace(/<rPh[\s\S]*?<\/rPh>/g, '');
  let text = '';
  for (const match of withoutPhonetics.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)) {
    text += decodeXml(match[1] ?? '');
  }
  return text;
}

/**
 * The format code behind each cell style, indexed the way a cell's `s`
 * attribute indexes it.
 *
 * Only the custom codes are collected. The built-in ids are dates, currencies
 * and percentages, none of which this reads back as anything but their stored
 * number; the one thing worth resolving — a number a spreadsheet *displays*
 * with leading zeros — is always a custom format, because there is no built-in
 * for it.
 */
function parseNumberFormats(xml: string | null): string[] {
  if (!xml) return [];

  const custom = new Map<string, string>();
  for (const match of xml.matchAll(/<numFmt\s[^>]*?\/?>/g)) {
    const id = attribute(match[0], 'numFmtId');
    const code = attribute(match[0], 'formatCode');
    if (id && code !== null) custom.set(id, decodeXml(code));
  }

  // `cellXfs` specifically: `cellStyleXfs` above it holds `<xf>` of its own,
  // and a cell's `s` counts into this list rather than that one.
  const section = /<cellXfs(?:\s[^>]*)?>([\s\S]*?)<\/cellXfs>/.exec(xml);
  if (!section) return [];

  const formats: string[] = [];
  for (const match of (section[1] ?? '').matchAll(/<xf\s[^>]*?\/?>/g)) {
    formats.push(custom.get(attribute(match[0], 'numFmtId') ?? '') ?? '');
  }
  return formats;
}

/**
 * A number the spreadsheet shows with leading zeros.
 *
 * A US postal code typed as 01234, or a padded reference, is stored as the
 * number 1234 plus a format saying to print it in five digits. Reading only the
 * number silently drops the zero from every such code in the file — and postal
 * code is one of the columns this imports.
 *
 * Only the all-zeros pattern is honored. Everything else a format can say —
 * currency, dates, thousands separators — is presentation over a value that is
 * already correct, and guessing at it would change data rather than recover it.
 */
function applyNumberFormat(raw: string, format: string): string {
  if (!/^0+$/.test(format)) return raw;
  const negative = raw.startsWith('-');
  const digits = negative ? raw.slice(1) : raw;
  if (!/^\d+$/.test(digits)) return raw;
  return `${negative ? '-' : ''}${digits.padStart(format.length, '0')}`;
}

function parseSheetRows(
  sheetXml: string,
  sharedStrings: readonly string[],
  numberFormats: readonly string[],
  maxRows: number,
): TableRow[] {
  const rows: TableRow[] = [];
  let previousLine = 0;

  for (const rowMatch of sheetXml.matchAll(/<row(\s[^>]*?)?>([\s\S]*?)<\/row>|<row(\s[^>]*?)\/>/g)) {
    if (rows.length >= maxRows) break;
    const row: string[] = [];
    // A worksheet omits its empty rows entirely, so row 5 can follow row 1 with
    // nothing in between. Its own `r` is the only thing that says which line of
    // the spreadsheet the person is looking at this actually is.
    const declared = Number.parseInt(attribute(rowMatch[1] ?? rowMatch[3] ?? '', 'r') ?? '', 10);
    const line = Number.isFinite(declared) && declared > previousLine ? declared : previousLine + 1;
    previousLine = line;

    for (const cellMatch of (rowMatch[2] ?? '').matchAll(/<c(\s[^>]*?)?(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attributes = cellMatch[1] ?? '';
      const body = cellMatch[2] ?? '';
      // Empty cells are omitted from the XML entirely, so a cell's column comes
      // from its reference ("C7"), never from its position among its siblings.
      const reference = attribute(attributes, 'r');
      const column = reference ? columnIndexOf(reference) : row.length;
      if (column >= MAX_COLUMNS) continue;
      while (row.length < column) row.push('');
      const styleIndex = Number.parseInt(attribute(attributes, 's') ?? '', 10);
      const format = Number.isFinite(styleIndex) ? (numberFormats[styleIndex] ?? '') : '';
      row[column] = cellValue(attribute(attributes, 't'), body, sharedStrings, format);
    }

    while (row.length > 0 && row[row.length - 1] === '') row.pop();
    rows.push({ line, cells: row });
  }

  return rows;
}

function cellValue(type: string | null, body: string, sharedStrings: readonly string[], format: string): string {
  if (type === 's') {
    const index = Number.parseInt(rawValue(body), 10);
    return sharedStrings[index] ?? '';
  }
  if (type === 'inlineStr') return collectText(body);
  if (type === 'b') return rawValue(body) === '1' ? 'TRUE' : 'FALSE';
  // `#N/A` and friends are the spreadsheet's own failure, not a value.
  if (type === 'e') return '';
  // Numbers are taken as the literal text the file stores rather than parsed
  // and re-printed: round-tripping through a JS number is how a 16-digit
  // account reference loses its last digit. The format is consulted only to put
  // back leading zeros the storage dropped.
  return applyNumberFormat(decodeXml(rawValue(body)), format);
}

function rawValue(body: string): string {
  const match = /<v(?:\s[^>]*)?>([\s\S]*?)<\/v>/.exec(body);
  return match?.[1] ?? '';
}

/** "BC7" is column 55 — base-26 over the letters, then zero-based. */
function columnIndexOf(reference: string): number {
  let index = 0;
  for (const char of reference) {
    const value = char.toUpperCase().charCodeAt(0) - 64;
    if (value < 1 || value > 26) break;
    index = index * 26 + value;
  }
  return Math.max(0, index - 1);
}

function attribute(tag: string, name: string): string | null {
  const match = new RegExp(`\\s${name.replace(':', '\\:')}="([^"]*)"`).exec(tag);
  return match?.[1] ?? null;
}

const XML_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

function decodeXml(value: string): string {
  if (!value.includes('&')) return value;
  return value.replace(/&(#x?[0-9A-Fa-f]+|[a-zA-Z]+);/g, (whole, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      return safeCodePoint(Number.parseInt(entity.slice(2), 16)) ?? whole;
    }
    if (entity.startsWith('#')) {
      return safeCodePoint(Number.parseInt(entity.slice(1), 10)) ?? whole;
    }
    return XML_ENTITIES[entity] ?? whole;
  });
}

function safeCodePoint(value: number): string | null {
  if (!Number.isFinite(value) || value < 0 || value > 0x10ffff) return null;
  return String.fromCodePoint(value);
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

export interface WriteXlsxOptions {
  sheetName: string;
  header: readonly string[];
  rows: ReadonlyArray<readonly XlsxCell[]>;
}

/**
 * A one-sheet workbook with a bold, frozen header row.
 *
 * The header is frozen and the columns are sized because the first thing
 * anybody does with an exported customer list is scroll it, and a file that
 * needs two adjustments before it can be read is a file that may as well have
 * been a CSV.
 *
 * Strings are written inline rather than pooled. A shared-string table is the
 * smaller encoding for repetitive data, and the difference does not survive the
 * deflate that wraps the whole part anyway.
 */
export async function writeXlsx(options: WriteXlsxOptions): Promise<Uint8Array> {
  return writeXlsxWorkbook([options]);
}

/** Multiple typed worksheets, using the same safe inline-string writer. */
export async function writeXlsxWorkbook(sheets: readonly WriteXlsxOptions[]): Promise<Uint8Array> {
  if (sheets.length === 0) throw new Error('A workbook needs at least one sheet.');
  const names = new Set<string>();
  const normalized = sheets.map((sheet, index) => {
    const base =
      sheet.sheetName
        .replace(/[\\/?*[\]:]/g, ' ')
        .replace(/^'+|'+$/g, '')
        .slice(0, 31) || `Sheet${index + 1}`;
    let name = base;
    for (let suffix = 2; names.has(name.toLowerCase()); suffix++) {
      const tail = ` (${suffix})`;
      name = base.slice(0, 31 - tail.length) + tail;
    }
    names.add(name.toLowerCase());
    if (sheet.rows.length > 1_048_575) throw new Error('Worksheet exceeds the Excel row limit.');
    return { ...sheet, sheetName: name };
  });
  const relationships = normalized
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    )
    .join('');
  const overrides = normalized
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join('');
  return zip([
    {
      name: '[Content_Types].xml',
      content: CONTENT_TYPES.replace(/<Override PartName="\/xl\/worksheets\/sheet1.xml"[^>]*\/>/, overrides),
    },
    { name: '_rels/.rels', content: ROOT_RELS },
    {
      name: 'xl/workbook.xml',
      content: workbookXml('Sheet1').replace(
        /<sheets>.*<\/sheets>/,
        `<sheets>${normalized.map((sheet, i) => `<sheet name="${escapeXml(sheet.sheetName)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`,
      ),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      content: WORKBOOK_RELS.replace(/<Relationship Id="rId1"[^>]*\/>/, relationships).replace(
        'Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"',
        `Id="rId${normalized.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"`,
      ),
    },
    { name: 'xl/styles.xml', content: STYLES },
    ...normalized.map((sheet, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, content: worksheetXml(sheet) })),
  ]);
}

function worksheetXml({ header, rows }: WriteXlsxOptions): string {
  const widths = columnWidths(header, rows);
  const sheet = [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
    '<sheetViews><sheetView workbookViewId="0">',
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>',
    '</sheetView></sheetViews>',
    `<cols>${widths.map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`).join('')}</cols>`,
    '<sheetData>',
    sheetRow(1, header, 1),
    ...rows.map((row, index) => sheetRow(index + 2, row, 0)),
    '</sheetData></worksheet>',
  ].join('');

  return sheet;
}

function sheetRow(rowNumber: number, cells: readonly XlsxCell[], styleId: number): string {
  const style = styleId > 0 ? ` s="${styleId}"` : '';
  const body = cells
    .map((cell, index) => {
      const reference = `${columnName(index)}${rowNumber}`;
      if (cell === null || cell === '') return '';
      if (typeof cell === 'number') {
        // A non-finite number has no cell representation; an empty cell is a
        // truer answer than the string "NaN" sitting in a numeric column.
        return Number.isFinite(cell) ? `<c r="${reference}"${style}><v>${cell}</v></c>` : '';
      }
      if (typeof cell === 'boolean') return `<c r="${reference}"${style} t="b"><v>${cell ? 1 : 0}</v></c>`;
      return `<c r="${reference}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(cell)}</t></is></c>`;
    })
    .join('');
  return `<row r="${rowNumber}">${body}</row>`;
}

/**
 * Wide enough to read, never so wide that one notes column owns the screen.
 *
 * Exported for its own test: it is the one piece of the writer whose output a
 * reader cannot check, because `<cols>` is a hint to the spreadsheet and is not
 * read back from the file.
 */
export function columnWidths(header: readonly string[], rows: ReadonlyArray<readonly XlsxCell[]>): number[] {
  // Sampling the first few hundred rows is enough to size a column, and keeps
  // the export from walking every cell of a large list twice.
  const sample = rows.slice(0, 500);
  // Sized from the widest row rather than from the header: a row longer than
  // its header would otherwise leave holes in this array, and a hole renders
  // as `width="undefined"` — a `<cols>` block Excel refuses to open.
  const count = sample.reduce((widest, row) => Math.max(widest, row.length), header.length);
  const widths = Array.from({ length: count }, (_, index) => header[index]?.length ?? 0);

  for (const row of sample) {
    row.forEach((cell, index) => {
      const length = cell === null ? 0 : String(cell).length;
      if (length > (widths[index] ?? 0)) widths[index] = length;
    });
  }

  return widths.map((width) => Math.min(50, Math.max(9, width + 2)));
}

function columnName(index: number): string {
  let name = '';
  let remaining = index;
  do {
    name = String.fromCharCode(65 + (remaining % 26)) + name;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return name;
}

/**
 * XML-safe text.
 *
 * The control-character strip is not cosmetic: XML 1.0 has no way to represent
 * most of them, escaped or not, and one stray `` pasted into a notes
 * field years ago is enough to make the whole workbook refuse to open.
 */
function escapeXml(value: string): string {
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

const CONTENT_TYPES = [
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">',
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
  '<Default Extension="xml" ContentType="application/xml"/>',
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
  '</Types>',
].join('');

const ROOT_RELS = [
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>',
  '</Relationships>',
].join('');

const WORKBOOK_RELS = [
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">',
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>',
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>',
  '</Relationships>',
].join('');

/**
 * Two cell formats: ordinary, and bold for the header.
 *
 * Every collection here is present and counted even where it holds only the
 * empty default. Excel treats a missing `fills` or `borders` list as a damaged
 * file rather than an empty one, and offers to repair it.
 */
const STYLES = [
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">',
  '<fonts count="2">',
  '<font><sz val="11"/><name val="Calibri"/></font>',
  '<font><b/><sz val="11"/><name val="Calibri"/></font>',
  '</fonts>',
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>',
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>',
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>',
  '<cellXfs count="2">',
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>',
  '</cellXfs>',
  '</styleSheet>',
].join('');

function workbookXml(sheetName: string): string {
  // Excel's own limits on a tab name: 31 characters, and none of the
  // characters it reserves for referring to sheets in formulas.
  const safeName = escapeXml(sheetName.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)) || 'Sheet1';
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"',
    ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets>`,
    '</workbook>',
  ].join('');
}

// ---------------------------------------------------------------------------
// ZIP
// ---------------------------------------------------------------------------

interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

const SIGNATURE_LOCAL = 0x04034b50;
const SIGNATURE_CENTRAL = 0x02014b50;
const SIGNATURE_EOCD = 0x06054b50;

/**
 * The archive's index, read from the end of the file where ZIP keeps it.
 *
 * Walking local headers from the front instead would appear to work and would
 * be wrong: an entry written by a streaming producer carries its sizes in a
 * trailing descriptor, so the header says zero and the walk runs off the rails.
 * The central directory is the authority.
 */
function readCentralDirectory(bytes: Uint8Array): Map<string, ZipEntry> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEndOfCentralDirectory(view, bytes.byteLength);
  if (eocd < 0) throw new XlsxError('notArchive', 'That file is not a spreadsheet — it is not a valid archive.');

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries = new Map<string, ZipEntry>();
  const decoder = new TextDecoder();

  for (let index = 0; index < count; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== SIGNATURE_CENTRAL) break;
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    entries.set(name, {
      name,
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

function findEndOfCentralDirectory(view: DataView, length: number): number {
  // The record is last, but a ZIP may carry a comment after it — up to the
  // 65535 bytes a 16-bit length can describe.
  const earliest = Math.max(0, length - 22 - 0xffff);
  for (let offset = length - 22; offset >= earliest; offset -= 1) {
    if (view.getUint32(offset, true) === SIGNATURE_EOCD) return offset;
  }
  return -1;
}

async function readEntry(bytes: Uint8Array, entry: ZipEntry): Promise<Uint8Array> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const header = entry.localHeaderOffset;
  if (header + 30 > bytes.byteLength || view.getUint32(header, true) !== SIGNATURE_LOCAL) {
    throw new XlsxError('damaged', `That spreadsheet is damaged — ${entry.name} is missing.`);
  }
  if (entry.uncompressedSize > MAX_PART_BYTES || entry.compressedSize > MAX_PART_BYTES) {
    throw new XlsxError('tooLarge', 'That spreadsheet is too large to read.');
  }

  // The local header repeats the name and may carry different extra data than
  // the central one, so its own lengths decide where the bytes start.
  const start = header + 30 + view.getUint16(header + 26, true) + view.getUint16(header + 28, true);
  const body = bytes.subarray(start, start + entry.compressedSize);

  if (entry.method === 0) return body;
  if (entry.method === 8) return inflateRaw(body);
  throw new XlsxError(
    'unsupportedCompression',
    `That spreadsheet uses a compression this app cannot read (method ${entry.method}).`,
  );
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  return transform(bytes, new DecompressionStream('deflate-raw'));
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  return transform(bytes, new CompressionStream('deflate-raw'));
}

async function transform(bytes: Uint8Array, stream: DecompressionStream | CompressionStream): Promise<Uint8Array> {
  // A fresh copy rather than the slice itself: the stream APIs take a view onto
  // an ordinary ArrayBuffer, and a subarray of whatever the caller handed over
  // carries its backing buffer's type with it.
  const chunk = new Uint8Array(bytes.byteLength);
  chunk.set(bytes);

  const writer = stream.writable.getWriter();
  const pumped = (async () => {
    await writer.write(chunk);
    await writer.close();
  })().catch(() => {
    // A failed transform fails the readable side too, and the loop below is
    // where it gets reported. Catching here only stops the same error being
    // raised a second time as an unhandled rejection.
  });

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  for (;;) {
    const next = await reader.read();
    if (next.done) break;
    chunks.push(next.value);
  }
  await pumped;

  return concat(chunks);
}

/** Builds the archive, deflating each part. */
async function zip(parts: ReadonlyArray<{ name: string; content: string }>): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const part of parts) {
    const name = encoder.encode(part.name);
    const raw = encoder.encode(part.content);
    const crc = crc32(raw);
    const deflated = await deflateRaw(raw);
    // Deflate can grow already-dense input. Storing it is always legal, and is
    // the smaller of the two whenever that happens.
    const stored = deflated.byteLength >= raw.byteLength;
    const body = stored ? raw : deflated;
    const method = stored ? 0 : 8;

    const local = new Uint8Array(30 + name.byteLength + body.byteLength);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, SIGNATURE_LOCAL, true);
    localView.setUint16(4, 20, true);
    // Bit 11 promises the name is UTF-8, which matters for nothing here but
    // costs nothing and is what every modern writer sets.
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(8, method, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, body.byteLength, true);
    localView.setUint32(22, raw.byteLength, true);
    localView.setUint16(26, name.byteLength, true);
    local.set(name, 30);
    local.set(body, 30 + name.byteLength);
    locals.push(local);

    const central = new Uint8Array(46 + name.byteLength);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, SIGNATURE_CENTRAL, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(10, method, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, body.byteLength, true);
    centralView.setUint32(24, raw.byteLength, true);
    centralView.setUint16(28, name.byteLength, true);
    centralView.setUint32(42, offset, true);
    central.set(name, 46);
    centrals.push(central);

    offset += local.byteLength;
  }

  const centralSize = centrals.reduce((total, entry) => total + entry.byteLength, 0);
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, SIGNATURE_EOCD, true);
  eocdView.setUint16(8, parts.length, true);
  eocdView.setUint16(10, parts.length, true);
  eocdView.setUint32(12, centralSize, true);
  eocdView.setUint32(16, offset, true);

  return concat([...locals, ...centrals, eocd]);
}

function concat(chunks: readonly Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.byteLength;
  }
  return out;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
